import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, useMediaQuery, Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

// Components
import PasswordScreen from './components/PasswordScreen';
import StockPremarketPage from './pages/StockPremarketPage';
import Sidebar from './components/layout/Sidebar';
import ChatMessage from './components/chat/ChatMessage';
import ChatInput from './components/chat/ChatInput';
import PromptsDrawer from './components/prompts/PromptsDrawer';
import CustomRoleDialog from './components/dialogs/CustomRoleDialog';
import AddCustomPromptDialog from './components/dialogs/AddCustomPromptDialog';

// Styles
import { theme } from './styles/theme';
import { MainContainer, ChatContainer, MessageList } from './styles/StyledComponents';

// Config
import { ROLES, ADDITIONAL_PROMPTS } from './config/constants';
import { sendPrompt } from './services/api';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    return authStatus;
  });

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    setIsAuthenticated(authStatus);
  }, []);

  // Core states
  const [selectedRole, setSelectedRole] = useState(ROLES.ROLE1.id);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // UI states
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [customRoleDialogOpen, setCustomRoleDialogOpen] = useState(false);
  const [isAddingPrompt, setIsAddingPrompt] = useState(false);
  const messageListRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  // Prompt states
  const [customPrompts, setCustomPrompts] = useState([]);
  const [favoritePrompts, setFavoritePrompts] = useState([]);
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [newPromptTitle, setNewPromptTitle] = useState('');
  const [newPromptText, setNewPromptText] = useState('');

  // Role states
  const [customRoleContent, setCustomRoleContent] = useState('');
  const [customRoleText, setCustomRoleText] = useState('');
  const [editingRoleId, setEditingRoleId] = useState(null);

  // Chat states
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');

  // Effects
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    setPromptsOpen(isLargeScreen);
  }, [isLargeScreen]);

  // Load chat history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('chatHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setChatHistory(parsedHistory);
        
        // If there was a selected chat, load it
        const lastSelectedChat = localStorage.getItem('selectedChat');
        if (lastSelectedChat) {
          const chat = parsedHistory.find(c => c.id === lastSelectedChat);
          if (chat) {
            setSelectedChat(chat.id);
            setMessages(chat.messages || []);
            setSelectedRole(chat.role || ROLES.ROLE1.id);
          }
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        localStorage.removeItem('chatHistory');
        localStorage.removeItem('selectedChat');
      }
    }
  }, []);

  // Save selected chat to localStorage
  useEffect(() => {
    if (selectedChat) {
      localStorage.setItem('selectedChat', selectedChat);
    } else {
      localStorage.removeItem('selectedChat');
    }
  }, [selectedChat]);

  useEffect(() => {
    localStorage.setItem('chatHistory', JSON.stringify(chatHistory));
  }, [chatHistory]);

  useEffect(() => {
    localStorage.setItem('customPrompts', JSON.stringify(customPrompts));
  }, [customPrompts]);

  // Handlers
  const handleNewChat = () => {
    // Reset all chat-related states
    setSelectedChat(null);
    setMessages([]);
    setInputValue('');
    // Reset to default role
    setSelectedRole(ROLES.ROLE1.id);
    // Remove selected chat from localStorage
    localStorage.removeItem('selectedChat');
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleRoleSelect = async (roleId) => {
    setSelectedRole(roleId);
    try {
      if (roleId === 'custom') return;

      const systemMessage = {
        role: 'system',
        content: `Role changed to: ${
          Object.values(ROLES).find(role => role.id === roleId)?.name || 'Unknown Role'
        }`
      };
      setMessages([systemMessage]);
    } catch (error) {
      console.error('Error loading role:', error);
    }
  };

  const handleSubmit = async () => {
    if (!inputValue.trim() || loading) return;

    const newMessage = { role: 'user', content: inputValue };
    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await sendPrompt(
        inputValue.trim(),
        selectedRole,
        messages
      );

      const updatedMessages = [
        ...messages,
        newMessage,
        { role: 'assistant', content: response }
      ];
      setMessages(updatedMessages);
      handleSaveChat(); // Save chat after messages are updated
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setMessages(prev => [
        ...prev,
        { 
          role: 'error',
          content: `Error: ${error.message}. Please try again or contact support if the issue persists.`
        }
      ]);
      handleSaveChat(); // Save chat even if there's an error
    } finally {
      setLoading(false);
    }
  };

  const handlePromptClick = (prompt) => {
    const newValue = inputValue + (inputValue ? '\n\n' : '') + prompt.prompt + '\n';
    setInputValue(newValue);
  };

  const handleLoadChat = (chat) => {
    setSelectedChat(chat.id);
    setMessages(chat.messages || []);
    setSelectedRole(chat.role || ROLES.ROLE1.id);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleDeleteChat = (chatId, event) => {
    event.stopPropagation();
    setChatHistory(prev => prev.filter(chat => chat.id !== chatId));
    if (selectedChat === chatId) {
      handleNewChat();
    }
  };

  const handleEditTitle = (chatId, currentTitle) => {
    setEditingTitle(chatId);
    setEditTitleValue(currentTitle);
  };

  const handleSaveTitle = (chatId) => {
    if (editTitleValue.trim()) {
      setChatHistory(prevHistory => {
        const existingChatIndex = prevHistory.findIndex(c => c.id === chatId);
        if (existingChatIndex >= 0) {
          const newHistory = [...prevHistory];
          newHistory[existingChatIndex] = chatHistory.find(c => c.id === chatId);
          newHistory[existingChatIndex].title = editTitleValue.trim();
          return newHistory;
        }
        return prevHistory;
      });
    }
    setEditingTitle(null);
    setEditTitleValue('');
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  const handleSavePrompt = () => {
    if (!newPromptTitle.trim() || !newPromptText.trim()) {
      return;
    }
    if (editingPromptId) {
      setCustomPrompts(prev => prev.map(p =>
        p.id === editingPromptId
          ? { ...p, title: newPromptTitle, prompt: newPromptText }
          : p
      ));
      setEditingPromptId(null);
    } else {
      const newPrompt = {
        id: Date.now().toString(),
        title: newPromptTitle,
        prompt: newPromptText,
        icon: '✨'
      };
      setCustomPrompts(prev => [...prev, newPrompt]);
    }
    setNewPromptTitle('');
    setNewPromptText('');
    setIsAddingPrompt(false);
  };

  const handleDeleteCustomPrompt = (promptId) => {
    setCustomPrompts(prev => prev.filter(p => p.id !== promptId));
  };

  const handleEditPrompt = (prompt) => {
    setEditingPromptId(prompt.id);
    setNewPromptTitle(prompt.title);
    setNewPromptText(prompt.prompt);
    setIsAddingPrompt(true);
  };

  const handleAddCustomRole = (roleText) => {
    const newRole = {
      id: `custom_${Date.now()}`,
      content: roleText.trim(),
      isCustom: true
    };
    setSelectedRole(newRole.id);
    setCustomRoleContent(newRole.content);
    setCustomRoleDialogOpen(false);
    setCustomRoleText('');
    setEditingRoleId(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleEditCustomRole = (roleId) => {
    setEditingRoleId(roleId);
    setCustomRoleText(customRoleContent);
    setCustomRoleDialogOpen(true);
  };

  const handleSaveCustomRole = () => {
    if (customRoleText.trim()) {
      if (editingRoleId) {
        setSelectedRole(editingRoleId);
        setCustomRoleContent(customRoleText.trim());
      } else {
        handleAddCustomRole(customRoleText);
      }
      setCustomRoleDialogOpen(false);
      setCustomRoleText('');
      setEditingRoleId(null);
    }
  };

  const handleClosePromptDialog = () => {
    setIsAddingPrompt(false);
    setNewPromptTitle('');
    setNewPromptText('');
    setEditingPromptId(null);
  };

  const handlePasswordSubmit = (isCorrect) => {
    setIsAuthenticated(isCorrect);
    if (isCorrect) {
      localStorage.setItem('isAuthenticated', 'true');
    } else {
      localStorage.removeItem('isAuthenticated');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
  };

  const handleSaveChat = () => {
    if (messages.length > 0) {
      const chatId = selectedChat || Date.now().toString();
      const chat = {
        id: chatId,
        title: messages[0]?.content.substring(0, 50) + '...',
        messages: messages,
        role: selectedRole,
        timestamp: new Date().toISOString(),
      };

      setChatHistory(prevHistory => {
        const existingChatIndex = prevHistory.findIndex(c => c.id === chatId);
        const newHistory = existingChatIndex >= 0
          ? prevHistory.map((c, index) => 
              index === existingChatIndex 
                ? { ...c, messages, title: chat.title, timestamp: chat.timestamp }
                : c
            )
          : [chat, ...prevHistory];
        
        // Save to localStorage immediately after state update
        localStorage.setItem('chatHistory', JSON.stringify(newHistory));
        return newHistory;
      });

      if (!selectedChat) {
        setSelectedChat(chatId);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            color: 'text.primary',
            p: 3,
          }}
        >
          <PasswordScreen onPasswordSubmit={handlePasswordSubmit} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/stock-premarket" element={<StockPremarketPage />} />
          <Route
            path="/"
            element={
              <MainContainer>
                <Sidebar 
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  selectedRole={selectedRole}
                  onRoleSelect={handleRoleSelect}
                  onCustomRole={() => setCustomRoleDialogOpen(true)}
                  chatHistory={chatHistory}
                  selectedChat={selectedChat}
                  onLoadChat={handleLoadChat}
                  onDeleteChat={handleDeleteChat}
                  onEditTitle={handleEditTitle}
                  editingTitle={editingTitle}
                  editTitleValue={editTitleValue}
                  setEditTitleValue={setEditTitleValue}
                  onSaveTitle={handleSaveTitle}
                  onCancelEdit={handleCancelEdit}
                  onLogout={handleLogout}
                  onNewChat={handleNewChat}
                />
                
                <Box sx={{ 
                  display: 'flex', 
                  flex: 1,
                  overflow: 'hidden'
                }}>
                  <ChatContainer>
                    <MessageList ref={messageListRef}>
                      {messages.map((message, index) => (
                        <ChatMessage
                          key={index}
                          message={message}
                          isUser={message.role === 'user'}
                        />
                      ))}
                    </MessageList>
                    
                    <ChatInput
                      value={inputValue}
                      onChange={setInputValue}
                      onSubmit={handleSubmit}
                      disabled={loading}
                    />
                  </ChatContainer>
                </Box>

                <PromptsDrawer
                  open={promptsOpen}
                  onClose={() => setPromptsOpen(false)}
                  customPrompts={customPrompts}
                  onAddPrompt={() => setIsAddingPrompt(true)}
                  onEditPrompt={handleEditPrompt}
                  onDeletePrompt={handleDeleteCustomPrompt}
                  favoritePrompts={favoritePrompts}
                  onFavoritePrompt={(promptId) => setFavoritePrompts(prev => 
                    prev.includes(promptId)
                      ? prev.filter(id => id !== promptId)
                      : [...prev, promptId]
                  )}
                  additionalPrompts={ADDITIONAL_PROMPTS}
                  onPromptClick={handlePromptClick}
                />

                <CustomRoleDialog
                  open={customRoleDialogOpen}
                  onClose={() => setCustomRoleDialogOpen(false)}
                  title={editingRoleId ? "Edit Role" : "Add New Role"}
                  roleText={customRoleText}
                  onRoleTextChange={(e) => setCustomRoleText(e.target.value)}
                  onSaveRole={handleSaveCustomRole}
                />

                <AddCustomPromptDialog
                  open={isAddingPrompt}
                  onClose={handleClosePromptDialog}
                  title={editingPromptId ? "Edit Prompt" : "Add New Prompt"}
                  promptTitle={newPromptTitle}
                  onPromptTitleChange={(e) => setNewPromptTitle(e.target.value)}
                  promptText={newPromptText}
                  onPromptTextChange={(e) => setNewPromptText(e.target.value)}
                  onSavePrompt={handleSavePrompt}
                />
              </MainContainer>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
